// Import custom error classes
import errorCodes from '@/constants/errorCodes';
import UserAccountExpiredException from '@/errors/UserAccountExpiredException';
import InvalidRequestDataException from '@/errors/InvalidRequestDataException';
import TokenMalformedError from '@/errors/TokenMalformedError';
import TokenExpiredException from '@/errors/TokenExpiredException';
import TokenInvalidException from '@/errors/TokenInvalidException';
import { AxiosError } from 'axios';
import appRouter from '@/router';

import ForbiddenPortalRequestedException from '@/errors/ForbiddenPortalRequestedException';
import EntityDoesNotExistException from '@/errors/EntityDoesNotExistException';
import EntityAlreadyExistsException from '@/errors/EntityAlreadyExistsException';
import InvalidCustomAttributeOperationException from '@/errors/InvalidCustomAttributeOperationException';

// Global error handler
export function handleError(error: AxiosError | Error) {
  if (
    error?.name === 'AuthRefreshError' ||
    ('isAuthRefreshError' in error && Boolean(error.isAuthRefreshError))
  ) {
    invalidRoute(error);
    return Promise.reject(error);
  }

  if ('response' in error && error.response) {
    // Extract error code from the response
    const errorData = error.response.data as {
      errorCode?: string | number;
      message?: string;
    };
    const errorCode = errorData.errorCode || error.response.status;
    // Switch case for different error codes
    switch (errorCode) {
      case errorCodes.UserAccountExpiredException: {
        return Promise.reject(new UserAccountExpiredException(error.message));
      }
      case errorCodes.InvalidRequestDataException: {
        return Promise.reject(new InvalidRequestDataException(error.message));
      }
      case errorCodes.TokenMalformedException: {
        invalidRoute(error);
        return Promise.reject(new TokenMalformedError(error.message));
      }
      case errorCodes.TokenExpiredException:
      case errorCodes.RefreshTokenExpiredException: {
        invalidRoute(error);
        return Promise.reject(new TokenExpiredException(error.message));
      }
      case errorCodes.ForbiddenBusinessProfileRequested:
      case errorCodes.InvalidBusinessProfileRequested:
      case errorCodes.Unauthorized:
      case errorCodes.TokenInvalidException: {
        invalidRoute(error);
        return Promise.reject(new TokenInvalidException(error.message));
      }
      case errorCodes.ForbiddenPortalRequested:
      case errorCodes.InvalidPortalRequestedException: {
        invalidRoute(error);
        return Promise.reject(
          new ForbiddenPortalRequestedException(error.message)
        );
      }
      case errorCodes.EntityDoesNotExistException: {
        return Promise.reject(new EntityDoesNotExistException(error.message));
      }
      case errorCodes.EntityAlreadyExistsError: {
        return Promise.reject(
          new EntityAlreadyExistsException(errorData.message || error.message)
        );
      }
      case errorCodes.InvalidCustomAttributeOperationException: {
        return Promise.reject(
          new InvalidCustomAttributeOperationException(
            errorData.message || error.message
          )
        );
      }
      default: {
        return Promise.reject(error);
      }
    }
  } else {
    return Promise.reject(error);
  }
}

const unauthenticatedApiEndpoints = ['portals'];

const accessErrors = new Set<number | string | undefined>([
  errorCodes.ForbiddenBusinessProfileRequested,
  errorCodes.ForbiddenPortalRequested,
  errorCodes.InvalidPortalRequestedException,
  errorCodes.InvalidBusinessProfileRequested
]);

async function invalidRoute(error: AxiosError | Error) {
  if (!appRouter) return;

  if (error instanceof AxiosError || error?.name === 'AxiosError') {
    const requestUrl = (error as AxiosError).config?.url;
    const errorData = (error as AxiosError).response?.data as
      | { errorCode?: string | number }
      | undefined;
    const errorCode = errorData?.errorCode || (error as AxiosError).response?.status;

    if (
      requestUrl &&
      (unauthenticatedApiEndpoints.some(ep => requestUrl.includes(ep)) ||
        accessErrors.has(errorCode))
    ) {
      appRouter.push({
        path: isForbiddenPortalError(error) ? '/no-access' : '/PageNotFound',
        replace: true
      });
      return;
    }
  }

  const currentRoutePath = appRouter.currentRoute.value.fullPath;
  let loginRoute = window.location.origin + '/login';
  if (currentRoutePath && currentRoutePath !== '/' && currentRoutePath !== '/login') {
    loginRoute += `?redirect=${encodeURIComponent(currentRoutePath)}`;
  }

  try {
    const { authService } = await import('@/services/api/authService');
    await authService.logout();
    return;
  } catch (error_) {
    console.error('Logout error:', error_);
  }

  appRouter.push({
    path: '/login',
    query: { redirect: currentRoutePath }
  });
}

export function isForbiddenPortalError(error: unknown): boolean {
  if (error instanceof ForbiddenPortalRequestedException) return true;
  if (error instanceof AxiosError || (error as AxiosError)?.name === 'AxiosError') {
    const axiosError = error as AxiosError;
    const errorData = axiosError.response?.data as { errorCode?: string | number } | undefined;
    const errorCode = errorData?.errorCode || axiosError.response?.status;
    return (
      errorCode === errorCodes.ForbiddenPortalRequested ||
      errorCode === errorCodes.InvalidPortalRequestedException
    );
  }
  return false;
}
